import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, ShieldCheck } from "lucide-react";

// Bumped Feb 2026 (v2): disclaimer copy is now exam-neutral. Bumping
// the key forces users who ack'd the old engineering-only text to re-ack
// the new NEET-inclusive version once. Old key `cetp_disclaimer_v1_ack`
// is intentionally orphaned — safe to leave, browsers GC unused keys.
const LS_KEY = "cetp_disclaimer_v2_ack";

// Routes where the consumer-facing disclaimer must NEVER show even if the user
// happens to hold a Supabase session — anonymous marketing/legal pages and the
// internal staff portal. Kept in one place so future routes are easy to add.
const PUBLIC_PREFIXES = [
  "/terms", "/privacy", "/refund", "/help/videos",
  "/verify-email", "/forgot-password", "/reset-password",
  "/partners", "/partner-signup", "/partner/apply",
  "/portal",
];

function hasAcked() {
  try { return !!localStorage.getItem(LS_KEY); } catch { return false; }
}

export default function DisclaimerModal() {
  const { session } = useAuth();
  const { pathname } = useLocation();
  const [ack, setAck] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Gating: modal only opens for an authenticated student on a non-public
  // route AND when they haven't yet acknowledged. Landing '/' is included
  // because a fresh signed-in user landing there before dashboard should
  // still ack once — but only if signed in.
  const onPublicRoute = PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  const shouldRender = !!session && !onPublicRoute && !hasAcked() && !dismissed;

  const accept = () => {
    try { localStorage.setItem(LS_KEY, new Date().toISOString()); } catch (_e) { /* storage unavailable */ }
    setDismissed(true);
  };

  if (!shouldRender) return null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v && ack) accept(); }}>
      {/*
        Mobile-first rewrite: the body content is HUGE on small screens
        (Android Chrome / Gmail in-app browser). The base Dialog primitive
        doesn't constrain height, so the Accept button used to fall off the
        viewport and the modal had no internal scroll — blocking new users.
        Fix: hard-cap the modal at ~90vh, make the long disclaimer scrollable
        on its own, and keep the checkbox + Accept button sticky at the
        bottom so they are ALWAYS in view.
      */}
      <DialogContent
        className="w-[calc(100vw-1.5rem)] max-w-lg p-0 gap-0 max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden"
        data-testid="disclaimer-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Sticky header */}
        <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3 border-b border-neutral-200 flex-shrink-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center mb-2.5">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-700" />
          </div>
          <DialogTitle className="font-display text-xl sm:text-2xl tracking-tight">Before you continue — please read</DialogTitle>
        </div>

        {/* Scrollable body */}
        <div
          className="overflow-y-auto overscroll-contain px-5 sm:px-6 py-4 flex-1 min-h-0"
          data-testid="disclaimer-modal-body"
        >
          <DialogDescription asChild>
            <div className="text-sm leading-relaxed text-slate-700 space-y-3">
              <p>
                AdmissionCrafts is a <strong>data-driven guidance tool</strong>, not a guarantee of admission. The groupings you
                will see (High Chance, Possible Chance, Low Chance) are derived from <strong>historical counselling cutoffs
                — MHT-CET CAP rounds for engineering aspirants and MCC AIQ + State DME rounds for NEET aspirants</strong> —
                and may not reflect the current year exactly.
              </p>
              <p>
                <strong>Always verify the official brochure</strong> for every preference you lock. For MHT-CET, use the
                official CAP brochure or the State CET Cell website. For NEET, use the MCC counselling brochure (AIQ) or
                your state DME&apos;s counselling notification (State quota). Branch / course names and codes can change between
                counselling years; AdmissionCrafts displays them for reference only.
              </p>
              <p>
                Our intent is simple: <strong>no student should miss out on a good college just because they didn&apos;t know it
                was within reach.</strong> Use this app to widen your awareness — then always cross-verify with the official
                brochure (CAP for CET, MCC / State DME for NEET) and an authorised counsellor before locking your final choice.
              </p>
              <p>
                <strong>We do not advertise, promote, deny or recommend any specific college.</strong> Every institute listed
                — engineering (CET) or medical / dental / AYUSH / allied-health (NEET) — is shown purely on the basis of
                its historical cutoff data versus your inputs. We have no commercial relationship with any of the colleges
                in our dataset.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Example:</span> a CET aspirant with 96 percentile in OBC will
                see a shortlist derived from past CAP closing percentiles; a NEET aspirant with AIR 15,000 in OPEN will see
                one derived from past MCC / State closing AIRs. Neither is a promise — both are windows on what past cutoffs
                looked like.
              </p>
              <p>
                For any discrepancy in data or to report an issue, please write to{" "}
                <a href="mailto:support@admissioncrafts.com" className="text-slate-900 underline font-semibold">
                  support@admissioncrafts.com
                </a>
                .
              </p>
              <p className="text-xs text-slate-500 pt-1 flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-600" />
                We are not affiliated with the State CET Cell, DTE Maharashtra, NTA, MCC, any State DME, or any listed college.
              </p>
            </div>
          </DialogDescription>
        </div>

        {/* Sticky footer with checkbox + Accept button — always in view. */}
        <div className="border-t border-neutral-200 bg-white px-5 sm:px-6 py-3 sm:py-4 flex-shrink-0 space-y-3">
          <div className="flex items-start gap-2.5 rounded-lg bg-slate-50 border border-neutral-200 p-3">
            <Checkbox
              id="ack-disclaimer"
              checked={ack}
              onCheckedChange={(v) => setAck(!!v)}
              data-testid="disclaimer-ack-checkbox"
              className="mt-0.5"
            />
            <label htmlFor="ack-disclaimer" className="text-xs text-slate-700 cursor-pointer leading-relaxed">
              I understand that predictions are advisory only and I agree to the{" "}
              <Link to="/terms" className="text-slate-900 underline font-semibold" target="_blank" rel="noreferrer" data-testid="disclaimer-terms-link">
                Terms &amp; Conditions
              </Link>
              .
            </label>
          </div>
          <Button
            onClick={accept}
            disabled={!ack}
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-not-allowed text-white"
            data-testid="disclaimer-accept-button"
          >
            I understand — continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
